import type { ElementType, PropsWithChildren, ReactElement } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { mergeSx } from 'components/ui/utilities/mergeSx';
import type { ButtonProps } from './Button.props';
import {
  buttonBrandOutlineSx,
  buttonBrandSx,
  buttonCompactSx,
  buttonDangerSx,
  buttonNeutralOutlineSx,
  buttonPositiveSx,
  buttonPrimaryGhostSx,
  buttonPrimaryOutlineSx,
  buttonPrimarySx,
  StyledButton,
} from './Button.styles';

const buttonVisualVariantSx = {
  brand: buttonBrandSx,
  brandOutline: buttonBrandOutlineSx,
  danger: buttonDangerSx,
  neutralOutline: buttonNeutralOutlineSx,
  positive: buttonPositiveSx,
  primary: buttonPrimarySx,
  primaryGhost: buttonPrimaryGhostSx,
  primaryOutline: buttonPrimaryOutlineSx,
} as const;

const buttonSizeVariantSx = {
  compact: buttonCompactSx,
} as const;

const Button = <RootComponent extends ElementType = 'button',>({
  children,
  disabled,
  endIcon,
  loading = false,
  loadingBehavior = 'replace',
  sizeVariant,
  startIcon,
  sx,
  visualVariant,
  ...props
}: PropsWithChildren<ButtonProps<RootComponent>>): ReactElement => {
  const isDisabled = disabled || loading;
  const shouldReplaceContent = loading && loadingBehavior === 'replace';
  const shouldShowInlineLoader = loading && loadingBehavior === 'inline';
  const presetSx = visualVariant ? buttonVisualVariantSx[visualVariant] : undefined;
  const sizePresetSx = sizeVariant ? buttonSizeVariantSx[sizeVariant] : undefined;
  const mergedSx = mergeSx(presetSx, sizePresetSx, sx);
  const accessibleLabel = props['aria-label']
    ?? (shouldReplaceContent && typeof children === 'string' ? children : undefined);

  return (
    <StyledButton
      aria-label={accessibleLabel}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      disabled={isDisabled}
      endIcon={loading ? undefined : endIcon}
      startIcon={loading ? undefined : startIcon}
      sx={mergedSx}
      {...props}
    >
      {shouldReplaceContent ? <CircularProgress size={20} color="inherit" /> : null}
      {shouldShowInlineLoader ? (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: 1,
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          <CircularProgress aria-hidden="true" color="inherit" size={18} thickness={5} />
          <Box component="span">{children}</Box>
        </Box>
      ) : null}
      {!loading ? children : null}
    </StyledButton>
  );
};

export default Button;
